import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, Typography, Space, Progress, Tooltip, Badge, Select } from 'antd';
import { Landmark } from 'lucide-react';
import {
    UploadOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    CloseOutlined,
    CheckCircleFilled,
    ExclamationCircleFilled,
    DeleteOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { Method_POST_UPLOAD, Method_POST_CONTEXTO } from '../../../../../service/api';
import { toast } from 'react-toastify';

const { Text, Title } = Typography;
const { Dragger } = Upload;

export default function ImportarArquivoModal({ open, onClose, tipoArquivo, onShowInstructions }) {
    // selectedFiles agora terá: { file, status: 'pending' | 'uploading' | 'success' | 'error', errorMsg?: string }
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    // Escolha da planilha Excel: '0' = Caixa; senão o código FEBRABAN do banco.
    // A planilha não diz de que banco é (diferente do OFX), então o cliente TEM
    // que escolher — senão não dá pra conciliar. undefined = ainda não escolheu.
    const [contaSel, setContaSel] = useState(undefined);
    // Lista COMPLETA de bancos (FEBRABAN), não só as contas já cadastradas.
    const [bancos, setBancos] = useState([]);
    const [loadingBancos, setLoadingBancos] = useState(false);
    const precisaConta = tipoArquivo === 'excel';
    const contaFaltando = precisaConta && (contaSel === undefined || contaSel === null || contaSel === '');

    // Carrega a lista de bancos ao abrir o modal de planilha.
    useEffect(() => {
        if (!open || tipoArquivo !== 'excel' || bancos.length > 0) return;
        let vivo = true;
        (async () => {
            setLoadingBancos(true);
            try {
                const res = await Method_POST_CONTEXTO('/cliente/conta-bancaria/banco/listar');
                const data = Array.isArray(res) ? res : [];
                data.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
                if (vivo) setBancos(data);
            } catch (e) {
                console.error('Erro ao carregar bancos:', e);
            } finally {
                if (vivo) setLoadingBancos(false);
            }
        })();
        return () => { vivo = false; };
    }, [open, tipoArquivo, bancos.length]);

    const validarOFX = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const conteudo = e.target.result;
                if (!/^OFXHEADER/i.test(conteudo) && !/<OFX>/i.test(conteudo)) {
                    reject("Arquivo OFX inválido. Certifique-se de que é um OFX XML ou SGML válido.");
                } else {
                    resolve(true);
                }
            };
            reader.onerror = () => reject("Erro ao ler o arquivo");
            reader.readAsText(file);
        });
    };

    const updateFileStatus = (uid, status, errorMsg = '') => {
        setSelectedFiles(prev => prev.map(item =>
            item.file.uid === uid ? { ...item, status, errorMsg } : item
        ));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            toast.error("Selecione pelo menos um arquivo antes de carregar");
            return;
        }

        // Planilha SEM conta escolhida = lixo pra conciliar → bloqueia.
        if (contaFaltando) {
            toast.error("Escolha a qual conta (Caixa ou banco) a planilha se refere.");
            return;
        }

        setUploading(true);
        let successCount = 0;
        let errorCount = 0;

        let url = tipoArquivo === "excel"
            ? "/contabil/lancamento/import/excel"
            : "/contabil/lancamento/import/ofx";

        // Excel: carimba a escolha em todas as linhas. '0' = Caixa; senão o banco.
        if (tipoArquivo === "excel") {
            if (String(contaSel) === "0") {
                url += "?caixa=true";
            } else {
                const banco = bancos.find(b => String(b.codigoFEBRABAN) === String(contaSel));
                url += `?bancoCodigo=${encodeURIComponent(contaSel)}`;
                if (banco && banco.nome) {
                    url += `&bancoNome=${encodeURIComponent(banco.nome)}`;
                }
            }
        }

        // Processa os arquivos um a um
        for (const item of selectedFiles) {
            if (item.status === 'success') continue; // Pula os que já deram certo

            const file = item.file;
            updateFileStatus(file.uid, 'uploading');

            if (tipoArquivo === "ofx") {
                try {
                    await validarOFX(file);
                } catch (err) {
                    updateFileStatus(file.uid, 'error', err);
                    errorCount++;
                    continue;
                }
            }

            try {
                const response = await Method_POST_UPLOAD(url, file);

                if (response.erro) {
                    const { mensagem } = response.erro;
                    updateFileStatus(file.uid, 'error', mensagem);
                    errorCount++;
                } else {
                    updateFileStatus(file.uid, 'success');
                    successCount++;
                }
            } catch (err) {
                console.error(`Erro ao enviar arquivo ${file.name}:`, err);
                updateFileStatus(file.uid, 'error', 'Erro na conexão com o servidor');
                errorCount++;
            }
        }

        if (successCount > 0) {
            toast.success(`${successCount} arquivo(s) processado(s) com sucesso!`);
        }

        setUploading(false);

        // Se todos deram certo, fecha em 2 segundos
        if (errorCount === 0 && successCount > 0) {
            setTimeout(() => {
                handleClose();
            }, 2000);
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        setUploading(false);
        setContaSel(undefined);
        onClose();
    };

    const handleRemoveFile = (uid) => {
        setSelectedFiles(prev => prev.filter(item => item.file.uid !== uid));
    };

    const handleRemoveAll = () => {
        setSelectedFiles([]);
    };

    const uploadProps = {
        accept: tipoArquivo === 'excel' ? ".xls,.xlsx" : tipoArquivo === 'ofx' ? ".ofx" : ".csv",
        multiple: true,
        showUploadList: false, // Vamos usar nossa lista customizada
        beforeUpload: (file) => {
            const fileExtension = file.name.split(".").pop().toLowerCase();
            const isValid = (tipoArquivo === "excel" && ["xls", "xlsx"].includes(fileExtension)) ||
                (tipoArquivo === "ofx" && fileExtension === "ofx") ||
                (tipoArquivo === "csv" && fileExtension === "csv");

            if (!isValid) {
                toast.error(`Tipo de arquivo inválido: ${file.name}`);
                return Upload.LIST_IGNORE;
            }

            // Evita duplicados
            setSelectedFiles(prev => {
                if (prev.find(item => item.file.name === file.name && item.file.size === file.size)) {
                    return prev;
                }
                return [...prev, { file, status: 'pending', errorMsg: '' }];
            });
            return false;
        },
    };

    const getIcon = (item) => {
        if (item.status === 'uploading') return <LoadingOutlined style={{ color: '#6366f1' }} />;
        if (item.status === 'success') return <CheckCircleFilled style={{ color: '#10b981' }} />;
        if (item.status === 'error') return <ExclamationCircleFilled style={{ color: '#ef4444' }} />;

        return tipoArquivo === 'excel' ?
            <FileExcelOutlined style={{ color: '#16a34a' }} /> :
            <FileTextOutlined style={{ color: '#2563eb' }} />;
    };

    return (
        <Modal
            title={null}
            open={open}
            onCancel={handleClose}
            footer={null}
            style={{ top: 80 }}
            width={540}
            className="premium-modal-v2"
            destroyOnClose
        >
            <div style={{ padding: '8px' }}>
                <header style={{ marginBottom: 24, textAlign: 'center' }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: 16,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        {tipoArquivo === 'excel' ? <FileExcelOutlined style={{ color: '#16a34a' }} /> : <FileTextOutlined style={{ color: '#2563eb' }} />}
                    </div>
                    <Title level={4} style={{ margin: 0 }}>
                        {tipoArquivo === 'excel' ? 'Importar Planilha Excel' : 'Importar Extrato OFX'}
                    </Title>
                    <Text type="secondary">Selecione os arquivos para processamento automático.</Text>

                    <div style={{ marginTop: 8 }}>
                        <Button
                            type="link"
                            size="small"
                            icon={<InfoCircleOutlined />}
                            onClick={onShowInstructions}
                            style={{ padding: 0 }}
                        >
                            Confira o formato aceito antes de importar
                        </Button>
                    </div>
                </header>

                {precisaConta && (
                    <div style={{ marginBottom: 20 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            <Landmark size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                            Esta planilha é de qual banco? <span style={{ color: '#ef4444' }}>*</span>
                        </Text>
                        <Select
                            showSearch
                            placeholder="Selecione Caixa ou o banco da planilha"
                            value={contaSel}
                            onChange={setContaSel}
                            style={{ width: '100%' }}
                            disabled={uploading}
                            loading={loadingBancos}
                            status={contaFaltando ? 'error' : ''}
                            dropdownMatchSelectWidth={false}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={[
                                { value: '0', label: '000 - DINHEIRO (CAIXA)' },
                                ...bancos.map(b => ({
                                    value: String(b.codigoFEBRABAN),
                                    label: `${b.codigoFEBRABAN} - ${b.nome}`
                                }))
                            ]}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            A planilha não informa o banco — sem isso não conseguimos conciliar.
                        </Text>
                    </div>
                )}

                <Dragger {...uploadProps} className="premium-upload-dragger" disabled={uploading}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#6366f1' }} />
                    </p>
                    <p className="ant-upload-text">Clique ou arraste os arquivos aqui</p>
                    <p className="ant-upload-hint">Suporte a múltiplos arquivos simultâneos</p>
                </Dragger>

                {selectedFiles.length > 0 && (
                    <div className="custom-file-list-container" style={{ marginTop: 24 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Text strong size="small">Arquivos Selecionados ({selectedFiles.length})</Text>
                            {!uploading && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={handleRemoveAll}
                                >
                                    Limpar Tudo
                                </Button>
                            )}
                        </div>

                        <div className="custom-file-list">
                            {selectedFiles.map((item) => (
                                <div key={item.file.uid} className={`custom-file-item status-${item.status}`}>
                                    <div className="file-info">
                                        <div className="file-icon">{getIcon(item)}</div>
                                        <div className="file-details">
                                            <Tooltip title={item.file.name}>
                                                <Text strong ellipsis style={{ maxWidth: 280, display: 'block' }}>
                                                    {item.file.name}
                                                </Text>
                                            </Tooltip>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                {(item.file.size / 1024).toFixed(1)} KB
                                                {item.errorMsg && <span style={{ color: '#ef4444', marginLeft: 8 }}>• {item.errorMsg}</span>}
                                            </Text>
                                        </div>
                                    </div>

                                    <div className="file-actions">
                                        {item.status === 'pending' && !uploading && (
                                            <Button
                                                type="text"
                                                icon={<CloseOutlined style={{ fontSize: 12 }} />}
                                                onClick={() => handleRemoveFile(item.file.uid)}
                                                className="remove-btn"
                                            />
                                        )}
                                        {item.status === 'uploading' && <Progress type="circle" percent={45} size={20} strokeColor="#6366f1" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{
                    marginTop: 32,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 12
                }}>
                    <Button
                        onClick={handleClose}
                        size="large"
                        className="premium-btn-secondary"
                        disabled={uploading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleUpload}
                        loading={uploading}
                        disabled={selectedFiles.length === 0 || contaFaltando}
                        size="large"
                        className="premium-btn-primary"
                    >
                        {uploading ? 'Processando...' : 'Processar Arquivos'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
